import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';

import { ChartContainer, type ChartConfig } from 'components/ui/chart';
import { Skeleton } from 'components/ui/skeleton';
import {
  useGetToolSummary,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IToolSummaryPayload } from 'features/chat/agent-chat-module/services/gpt-chat.service';

const parseBackendUtc = (value: string) => new Date(value);

export function ToolDashboardChart({
  toolSummaryPayload,
  period,
  setTotalCalls,
  setSuccessfulCalls,
  setFailedCalls,
}: {
  toolSummaryPayload: IToolSummaryPayload;
  period: '24h' | '7d' | '30d';
  setTotalCalls: (value: number) => void;
  setSuccessfulCalls: (value: number) => void;
  setFailedCalls: (value: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const { isLoading, data: toolSummary } = useGetToolSummary(toolSummaryPayload);

  useEffect(() => {
    if (!isLoading) {
      setTotalCalls(toolSummary?.data?.total_calls || 0);
      setSuccessfulCalls(toolSummary?.data?.successful_calls || 0);
      setFailedCalls(toolSummary?.data?.failed_calls || 0);
    }
  }, [isLoading, setFailedCalls, setSuccessfulCalls, setTotalCalls, toolSummary]);

  const chartConfig = useMemo(
    () =>
      ({
        requests: {
          label: t('REQUESTS') || 'Requests',
          color: 'hsl(var(--primary))',
        },
      }) satisfies ChartConfig,
    [t]
  );

  const requestsData = useMemo(() => {
    const logs = toolSummary?.data?.logs || [];

    if (period === '24h') {
      const buckets = Array.from({ length: 24 }, (_, index) => ({
        duration: `${index.toString().padStart(2, '0')}:00`,
        requests: 0,
      }));

      logs.forEach((log) => {
        const hour = parseBackendUtc(log.created_at).getHours();
        if (hour >= 0 && hour < 24) {
          buckets[hour].requests += 1;
        }
      });

      return buckets;
    }

    const days = period === '7d' ? 7 : 30;
    const buckets = Array.from({ length: days }, (_, index) => {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() - (days - 1 - index));

      return {
        fullDate: date.toDateString(),
        duration: date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
        requests: 0,
      };
    });

    const bucketMap = new Map(buckets.map((bucket) => [bucket.fullDate, bucket]));

    logs.forEach((log) => {
      const fullDate = parseBackendUtc(log.created_at).toDateString();
      const bucket = bucketMap.get(fullDate);
      if (bucket) {
        bucket.requests += 1;
      }
    });

    return buckets.map(({ duration, requests }) => ({ duration, requests }));
  }, [i18n.language, period, toolSummary]);

  if (isLoading) {
    return (
      <div className="h-full max-h-[320px] w-full overflow-x-auto pb-2">
        <div className="flex h-full min-w-[600px] items-end gap-2">
          {Array.from({ length: 24 }).map((_, index) => (
            <Skeleton
              key={index}
              className="w-full rounded-t-md"
              style={{
                height: `${[35, 60, 45, 75, 50, 80, 55, 90, 65, 40, 70, 30][index % 12]}%`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full max-h-[320px] w-full overflow-x-auto">
      <div className="h-full min-w-[600px]">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <BarChart data={requestsData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="duration"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval="preserveStartEnd"
              allowDecimals={false}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              domain={[0, 'dataMax']}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted))' }}
              content={({ active, payload }) => {
                if (!(active && payload && payload.length)) {
                  return null;
                }

                return (
                  <div className="rounded-lg border bg-background p-2 shadow-sm">
                    <div className="flex flex-col">
                      <span className="text-[0.70rem] uppercase text-muted-foreground">
                        {t('REQUESTS') || 'Requests'}
                      </span>
                      <span className="font-bold text-foreground">{payload[0].value}</span>
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="requests" fill="var(--color-requests)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  );
}
